import { useEffect, useRef } from "react";

export default function HeroSection() {
  const bgRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bg = bgRef.current;
    const overlay = overlayRef.current;
    if (!bg || !overlay) return;

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const rate = scrolled * 0.35;
      bg.style.transform = `translateY(${rate}px)`;
      const opacity = Math.min(1, scrolled / 600);
      overlay.style.opacity = String(1 + opacity * 0.6);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative h-[520px] md:h-[700px] lg:h-screen overflow-hidden">
      {/* Background Image */}
      <div
        ref={bgRef}
        className="absolute inset-0 h-[120%] w-full"
        style={{ willChange: "transform" }}
      >
        <img
          src="/photos/hero-golden-gate.webp"
          alt="GOOD RIDES LA private driver Ece Czerw with a black Cadillac Escalade at the Golden Gate Bridge"
          className="w-full h-full object-cover object-[68%_center] md:object-[60%_center]"
        />
      </div>

      {/* Directional overlay: darker on the left for text legibility,
          lighter on the right so the driver stays visible and well lit. */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/15"
        style={{ willChange: "opacity" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/25 pointer-events-none" />

      {/* Floating decorative particles */}
      <div className="absolute inset-0 z-[5] pointer-events-none overflow-hidden">
        <div className="absolute top-[15%] left-[8%] w-2 h-2 rounded-full bg-primary-400/40 animate-float" />
        <div className="absolute top-[25%] right-[12%] w-1.5 h-1.5 rounded-full bg-primary-300/30 animate-float-delay" />
        <div className="absolute top-[60%] left-[20%] w-1 h-1 rounded-full bg-accent-300/40 animate-float" />
        <div className="absolute top-[40%] right-[25%] w-2.5 h-2.5 rounded-full bg-primary-400/25 animate-float-delay" />
        <div className="absolute top-[70%] right-[10%] w-1.5 h-1.5 rounded-full bg-primary-300/35 animate-float" />
        <div className="absolute top-[35%] left-[40%] w-1 h-1 rounded-full bg-accent-400/30 animate-float-delay" />
      </div>

      {/* Light streak accents */}
      <div className="absolute inset-0 z-[4] pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent animate-fade-in" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/15 to-transparent animate-fade-in" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center lg:items-start justify-center h-full px-4 md:px-8 lg:px-14 xl:px-20 text-center lg:text-left max-w-7xl mx-auto w-full">
        {/* Top badge */}
        <div className="animate-fade-in-up mb-6 md:mb-8">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-background-50/25 text-background-50 text-xs md:text-sm font-medium tracking-[0.2em] uppercase backdrop-blur-md bg-background-50/5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
            <span>Premium Driver Service &bull; Los Angeles</span>
          </div>
        </div>

        {/* Main Slogan */}
        <div className="max-w-4xl mx-auto lg:mx-0 mb-6 md:mb-8">
          <h1 className="animate-fade-in-up-delay-1 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.05] text-background-50">
            Everybody
            <br />
            <span className="animate-shimmer-gold">
              deserves a good ride
            </span>
          </h1>
        </div>

        {/* Decorative line */}
        <div className="animate-scale-in mb-6 md:mb-8 w-16 md:w-24 h-[2px] bg-gradient-to-r from-primary-400/0 via-primary-400 to-primary-400/0 lg:from-primary-400 lg:via-primary-400 lg:to-transparent" />

        {/* Subtitle */}
        <p className="animate-fade-in-up-delay-2 text-background-50/80 text-sm md:text-base lg:text-lg max-w-lg md:max-w-xl mx-auto lg:mx-0 leading-relaxed mb-8 md:mb-10">
          From LAX airport transfers to Hollywood nightlife, Beverly Hills
          shopping to Malibu coast tours. Experience Los Angeles with
          unmatched comfort, safety, and style.
        </p>

        {/* CTA Buttons */}
        <div className="animate-fade-in-up-delay-3 flex flex-col sm:flex-row items-center lg:justify-start gap-3 sm:gap-4">
          <a
            href="#reservation"
            className="group relative inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap animate-pulse-glow"
          >
            <span>Book Your Ride</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a
            href="#services"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-background-50/25 text-background-50 text-sm md:text-base font-medium hover:bg-background-50/10 hover:border-background-50/40 transition-all duration-300 whitespace-nowrap backdrop-blur-sm"
          >
            <span>Explore Services</span>
            <i className="ri-arrow-down-line" />
          </a>
        </div>

        {/* Bottom info pills */}
        <div className="animate-fade-in-up-delay-4 flex flex-wrap items-center justify-center lg:justify-start gap-3 md:gap-5 mt-10 md:mt-14">
          <div className="flex items-center gap-2 text-background-50/70 text-xs md:text-sm">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm">
              <i className="ri-shield-check-line text-primary-400 text-sm md:text-base" />
            </div>
            <span>Licensed &amp; Insured</span>
          </div>
          <div className="flex items-center gap-2 text-background-50/70 text-xs md:text-sm">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm">
              <i className="ri-star-fill text-primary-400 text-sm md:text-base" />
            </div>
            <span>4.9 Rating</span>
          </div>
          <div className="flex items-center gap-2 text-background-50/70 text-xs md:text-sm">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm">
              <i className="ri-time-line text-primary-400 text-sm md:text-base" />
            </div>
            <span>24/7 Available</span>
          </div>
          <div className="flex items-center gap-2 text-background-50/70 text-xs md:text-sm">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm">
              <i className="ri-car-line text-primary-400 text-sm md:text-base" />
            </div>
            <span>Luxury Fleet</span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-fade-in-up-delay-4">
          <div className="flex flex-col items-center gap-2 text-background-50/50 text-xs">
            <span className="tracking-widest uppercase">Scroll</span>
            <div className="w-5 h-8 rounded-full border border-background-50/30 flex items-start justify-center p-1">
              <div className="w-1 h-2 rounded-full bg-primary-400 animate-bounce" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}